import { Injectable } from '@angular/core';
import { TvaModel } from 'src/app/core/models/tva.model';
import { HttpClient } from '@angular/common/http';
import { BaseService } from 'src/app/core/base/base.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { map, tap } from 'rxjs/operators';
import { OrderAdditionalCostModel } from '../../../../core/models/order.model';
import { RateList } from '../../../../core/models/generic-tva.model';
import { AuthService } from '../../../../core/services/auth.service';
import { GenericTvaService } from '../generic-tva/generic-tva.service';
import { AbstractControl } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class TvaService extends BaseService<TvaModel> {
  defaultSort = 'createdAt';
  constructor(
    public http: HttpClient,
    private genericTvaService: GenericTvaService,
    public authService: AuthService,
  ) {
    super(http, 'tva', authService);
  }

  getRateByDate(tva: TvaModel, date: Date = new Date()): RateList {
    return this.genericTvaService.getCurrentRate(tva?.genericTva?.rateList, date);
  }

  /**
   * Libellé d'affichage d'une TVA : « Taux normal 20.00% ».
   * Les documents TVA n'ont pas de `label` propre : il vit sur la TVA générique.
   */
  getDisplayLabel(tva: TvaModel, date: Date = new Date()): string {
    const label = tva?.genericTva?.label ?? '';
    const rate = this.getRateByDate(tva, date)?.rate;
    return typeof rate === 'number' ? `${label} ${rate.toFixed(2)}%`.trim() : label;
  }

  /** Copie de la TVA portant son `display` (voir getDisplayLabel). */
  withDisplay(tva: TvaModel): TvaModel {
    return tva ? { ...tva, display: this.getDisplayLabel(tva) } : tva;
  }

  /**
   * Liste des TVA enrichie d'un `display`, pour les autocompletes (`displayProperty="display"`).
   * Si `control` est fourni, sa valeur courante (TVA peuplée ou simple identifiant, selon l'endpoint
   * d'origine) est remplacée par l'élément correspondant de la liste, afin qu'elle s'affiche.
   */
  findAllForAutocomplete(search = '', control?: AbstractControl): Observable<TvaModel[]> {
    return this.findAll(null, null, undefined, undefined, search).pipe(
      map(res => res.data.map(tva => this.withDisplay(tva))),
      tap(tvas => control && this.syncControlWithList(control, tvas)),
    );
  }

  private syncControlWithList(control: AbstractControl, tvas: TvaModel[]): void {
    const current = control.value;
    const currentId: string = current?._id ?? current;
    if (!currentId) {
      return;
    }
    const match = tvas.find(t => t._id === currentId);
    if (match) {
      // emitEvent false : présélection, pas une saisie utilisateur
      control.setValue(match, { emitEvent: false });
    }
  }

  // PRED-3081 : comptes TVA saisis manuellement (numéro + libellé)
  addAccountingAccount(
    idTva: string,
    data: { accountNumber: string; label: string },
  ): Observable<ObjectApiInterface<TvaModel>> {
    return this.withCurrentTva(
      this.http.post<ObjectApiInterface<TvaModel>>(`${this.baseUrlApi}/${idTva}/addAccountingAccount`, data),
    );
  }

  updateAccountingAccount(
    idTva: string,
    data: { currentAccountNumber: string; accountNumber: string; label: string },
  ): Observable<ObjectApiInterface<TvaModel>> {
    return this.withCurrentTva(
      this.http.put<ObjectApiInterface<TvaModel>>(`${this.baseUrlApi}/${idTva}/updateAccountingAccount`, data),
    );
  }

  removeAccountingAccount(idTva: string, data: { accountNumber: string }): Observable<ObjectApiInterface<TvaModel>> {
    return this.withCurrentTva(
      this.http.post<ObjectApiInterface<TvaModel>>(`${this.baseUrlApi}/${idTva}/removeAccountingAccount`, data),
    );
  }

  /** La TVA renvoyée devient la TVA courante : la page de détail se met à jour via entity$. */
  private withCurrentTva(request$: Observable<ObjectApiInterface<TvaModel>>): Observable<ObjectApiInterface<TvaModel>> {
    return request$.pipe(
      map(x => {
        this._entity.next(x.data);
        return x;
      }),
    );
  }
}
